import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Button } from '../../../../components-v2/Button';
import { Input, InputAddon, InputGroup } from '../../../../components-v2/Input';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Toggle } from '../../../../components/Toggle';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { DonationSurveyFormData } from '../utils';

/**
 * Parses currency input and converts to numeric value.
 *
 * Handles edge cases:
 * - Strips all non-numeric characters except decimal point
 * - Empty string → 0
 * - Single decimal point "." → 0
 * - Invalid numbers → no change (preserves previous value)
 * - Valid numbers → updates field
 *
 * Examples:
 * - "$123.45" → 123.45
 * - "abc" → no change
 * - "." → 0
 * - "" → 0
 *
 * @param inputValue - Raw input string (may contain $, commas, etc.)
 * @param onChange - React Hook Form field onChange handler
 */
const parseCurrencyInput = (inputValue: string, onChange: (value: number) => void): void => {
	const numericValue = inputValue.replace(/[^0-9.]/g, '');
	const parsedValue = parseFloat(numericValue);

	if (numericValue === '' || numericValue === '.') {
		onChange(0);
	} else if (!isNaN(parsedValue)) {
		onChange(parsedValue);
	}
	// If invalid (isNaN), do nothing - preserves previous value
};

type FrequencyType = 'oneTime' | 'monthly' | 'annual';

interface Props {
	/** Type of frequency this section is for ('oneTime', 'monthly', or 'annual') */
	frequencyType: FrequencyType;
	/** Display name for this frequency (e.g., "One-Time", "Monthly", "Annual") */
	displayName: string;
	/** Whether the survey is archived. When true, all inputs are disabled (read-only mode) */
	isArchived: boolean;
}

/**
 * Maps frequency types to their corresponding form field paths.
 * Used to generate type-safe field names for react-hook-form Controller components.
 */
const frequencyFieldNames = {
	oneTime: {
		enabled: 'oneTimeSettings.enabled' as const,
		hasDonationValues: 'oneTimeSettings.hasDonationValues' as const,
		donationValues: 'oneTimeSettings.donationValues' as const,
		allowCustomDonationAmount: 'oneTimeSettings.allowCustomDonationAmount' as const,
		hasMinimumDonationAmount: 'oneTimeSettings.hasMinimumDonationAmount' as const,
		minimumDonationAmount: 'oneTimeSettings.minimumDonationAmount' as const,
	},
	monthly: {
		enabled: 'monthlySettings.enabled' as const,
		hasDonationValues: 'monthlySettings.hasDonationValues' as const,
		donationValues: 'monthlySettings.donationValues' as const,
		allowCustomDonationAmount: 'monthlySettings.allowCustomDonationAmount' as const,
		hasMinimumDonationAmount: 'monthlySettings.hasMinimumDonationAmount' as const,
		minimumDonationAmount: 'monthlySettings.minimumDonationAmount' as const,
	},
	annual: {
		enabled: 'annualSettings.enabled' as const,
		hasDonationValues: 'annualSettings.hasDonationValues' as const,
		donationValues: 'annualSettings.donationValues' as const,
		allowCustomDonationAmount: 'annualSettings.allowCustomDonationAmount' as const,
		hasMinimumDonationAmount: 'annualSettings.hasMinimumDonationAmount' as const,
		minimumDonationAmount: 'annualSettings.minimumDonationAmount' as const,
	},
};

/**
 * DonationFrequencySettingsSection Component
 *
 * Renders donation settings for a specific frequency (one-time, monthly, or annual).
 * Manages donation amounts, custom donation options, and minimum amount requirements.
 *
 * Features:
 * - Toggle to enable/disable the frequency (all frequencies can be toggled)
 * - "Last one standing" rule: At least one frequency must always be enabled (enforced by parent)
 * - Suggested donation amounts with add/remove controls
 * - Custom donation amount option
 * - Minimum donation amount (one-time only)
 * - Currency formatting with dollar sign display
 * - Form validation and error display
 * - Disabled state when survey is archived
 *
 * @param frequencyType - Type of frequency this section is for
 * @param displayName - Display name for this frequency (e.g., "One-Time", "Monthly", "Annual")
 * @param isArchived - Whether the survey is archived
 */
export const DonationFrequencySettingsSection: React.FC<Props> = ({ frequencyType, displayName, isArchived }) => {
	const {
		control,
		watch,
		formState: { errors },
	} = useFormContext<DonationSurveyFormData>();

	const fieldNames = frequencyFieldNames[frequencyType];
	const formValues = watch();

	// Get the nested form values for this frequency
	// Maps frequencyType to the corresponding settings object in the form data
	const frequencySettings =
		frequencyType === 'oneTime'
			? formValues.oneTimeSettings
			: frequencyType === 'monthly'
				? formValues.monthlySettings
				: formValues.annualSettings;

	const {
		fields: valueFields,
		append,
		remove,
	} = useFieldArray({
		control,
		name: fieldNames.donationValues,
	});

	// Get nested errors for this frequency
	// Extracts validation errors from the nested form structure based on frequency type
	const frequencyErrors =
		frequencyType === 'oneTime'
			? errors.oneTimeSettings
			: frequencyType === 'monthly'
				? errors.monthlySettings
				: errors.annualSettings;

	return (
		<section className={css(styleSheet.section)}>
			<FormFieldWrapper
				name={fieldNames.enabled}
				control={control}
				title={`Allow ${displayName} Donations`}
				description={
					frequencyType === 'oneTime'
						? `Setup one-time donations`
						: `Donors can setup recurring ${frequencyType === 'monthly' ? 'monthly' : 'annual'} donations`
				}
			>
				{({ value, onChange }) => (
					<Toggle
						isOn={value}
						onToggleCheckChanged={onChange}
						uncheckedColor={THEME.colors.background.dark}
						disabled={isArchived}
					/>
				)}
			</FormFieldWrapper>

			{frequencySettings?.enabled && (
				<>
					<FormFieldWrapper
						name={fieldNames.hasDonationValues}
						control={control}
						title={`Set ${displayName} Donation Amount`}
						description='Specify the donation amounts you would like to accept'
					>
						{({ value, onChange }) => (
							<Toggle
								isOn={value}
								onToggleCheckChanged={onChange}
								uncheckedColor={THEME.colors.background.dark}
								disabled={isArchived}
							/>
						)}
					</FormFieldWrapper>

					<div className={css(styleSheet.donationValuesContainer)}>
						{valueFields.map((field, index) => (
							<div key={field.id} className={css(styleSheet.donationValue)}>
								<Controller
									name={`${fieldNames.donationValues}.${index}.value` as any}
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<InputGroup>
											<Input
												type='text'
												disabled={isArchived}
												value={fieldProps.value ? `$${fieldProps.value}` : ''}
												onChange={e => parseCurrencyInput(e.target.value, fieldProps.onChange)}
												placeholder='$0'
											/>
											<InputAddon onClick={() => remove(index)} style={{ cursor: 'pointer' }}>
												<XIcon />
											</InputAddon>
										</InputGroup>
									)}
								/>

								{frequencyErrors?.donationValues?.[index]?.value?.message && (
									<span className={css(styleSheet.errorMessage)}>
										{frequencyErrors.donationValues[index].value.message}
									</span>
								)}
							</div>
						))}

						<Button
							type='button'
							variant='link'
							onClick={() => append({ value: 0 })}
							disabled={isArchived || !frequencySettings?.hasDonationValues}
							styles={[styleSheet.addOptionButton]}
						>
							+ Add Option
						</Button>

						{frequencyErrors?.donationValues?.message &&
							typeof frequencyErrors.donationValues.message === 'string' &&
							valueFields.filter(Boolean).length === 0 && (
								<span className={css(styleSheet.errorMessage)}>{frequencyErrors.donationValues.message}</span>
							)}
					</div>

					<FormFieldWrapper
						name={fieldNames.allowCustomDonationAmount}
						control={control}
						title='Allow Custom Donation Amount'
						description='Allow donors to specify how much they want to give'
					>
						{({ value, onChange }) => (
							<Toggle
								isOn={value}
								onToggleCheckChanged={onChange}
								uncheckedColor={THEME.colors.background.dark}
								disabled={isArchived}
							/>
						)}
					</FormFieldWrapper>

					{/* Minimum donation amount is only available for one-time donations, not for recurring */}
					{frequencySettings?.allowCustomDonationAmount && frequencyType === 'oneTime' && (
						<>
							<FormFieldWrapper
								name={fieldNames.hasMinimumDonationAmount}
								control={control}
								title='Set Minimum Donation Amount'
								description='Specify the minimum donation amount you would like to receive'
							>
								{({ value, onChange }) => (
									<Toggle
										isOn={value}
										onToggleCheckChanged={onChange}
										uncheckedColor={THEME.colors.background.dark}
										disabled={isArchived}
									/>
								)}
							</FormFieldWrapper>

							{frequencySettings?.hasMinimumDonationAmount && (
								<FormFieldWrapper
									name={fieldNames.minimumDonationAmount}
									control={control}
									title='Minimum Amount'
									description='Enter the minimum donation amount'
									controllerWrapperStyles={[styleSheet.controllerWrapper]}
								>
									{({ value, onChange }) => (
										<Input
											disabled={
												isArchived ||
												!frequencySettings?.hasMinimumDonationAmount ||
												!frequencySettings?.allowCustomDonationAmount
											}
											styles={[styleSheet.minimumDonationAmountInput]}
											type='text'
											value={value ? `$${value}` : ''}
											onChange={e => parseCurrencyInput(e.target.value, onChange)}
											placeholder='$0'
										/>
									)}
								</FormFieldWrapper>
							)}
						</>
					)}
				</>
			)}
		</section>
	);
};
